import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Card from '../common/Card/Card';
import Button from '../common/Button/Button';
import BoundingBoxMap from '../common/BoundingBoxMap/BoundingBoxMap';
import { useAuth } from '../../hooks/useAuth';
import { Search } from '../../types/search.types';
import { TOKEN_STORAGE_KEY } from '../../utils/auth';
import {
  Map,
  User,
  Calendar,
  Download,
} from 'lucide-react';

const AdminSearchDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const searchId = parseInt(id || '0', 10);
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [search, setSearch] = useState<Search | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFormat, setDownloadingFormat] = useState<string | null>(null);

  useEffect(() => {
    const fetchSearchDetails = async () => {
      if (!isAuthenticated || !user?.is_admin) {
        return;
      }

      try {
        const token = localStorage.getItem(TOKEN_STORAGE_KEY);
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
        const apiUrl = process.env.REACT_APP_API_URL || '';

        const response = await fetch(`${apiUrl}/api/admin/searches/${searchId}`, { headers });
        if (response.ok) {
          const data = await response.json();
          setSearch(data);
        } else {
          setError(`Failed to fetch search details: ${response.status}`);
        }
      } catch (error) {
        setError('Error fetching search details');
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchDetails();
  }, [searchId, isAuthenticated, user]);

  const handleDownload = async (format: 'csv' | 'excel') => {
    if (!search || !isAuthenticated) return;

    setDownloadingFormat(format);
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      const apiUrl = process.env.REACT_APP_API_URL || '';
      
      const response = await fetch(
        `${apiUrl}/api/searches/${searchId}/export?format=${format}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const fileExtension = format === 'csv' ? 'csv' : 'xlsx';
      const fileName = `${search.search_query}_${search.city_name}_results.${fileExtension}`;
      
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      setError(`Failed to download ${format.toUpperCase()} file`);
    } finally {
      setDownloadingFormat(null);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <Card>
        <div className="flex justify-center items-center py-12">
          <span className="text-gray-600">Loading search details...</span>
        </div>
      </Card>
    );
  }

  // Show error state
  if (error) {
    return (
      <Card>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900">Error</h3>
          <p className="mt-1 text-gray-500">{error}</p>
          <div className="mt-6">
            <Link to="/admin">
              <Button>Back to Admin Panel</Button>
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  // Show not found state
  if (!search) {
    return (
      <Card>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900">Search not found</h3>
          <p className="mt-1 text-gray-500">The search you're looking for doesn't exist.</p>
          <div className="mt-6">
            <Link to="/admin">
              <Button>Back to Admin Panel</Button>
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2 sm:mb-0">
          Search Details: {search.search_query}
        </h2>
        <div className="flex flex-col sm:flex-row gap-2 mt-2 sm:mt-0">
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleDownload('csv')}
              disabled={downloadingFormat !== null}
              className="flex items-center gap-1"
            >
              <Download className="h-4 w-4" />
              {downloadingFormat === 'csv' ? 'Downloading...' : 'CSV'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleDownload('excel')}
              disabled={downloadingFormat !== null}
              className="flex items-center gap-1"
            >
              <Download className="h-4 w-4" />
              {downloadingFormat === 'excel' ? 'Downloading...' : 'XLSX'}
            </Button>
          </div>
          <Link to="/admin">
          <Button variant="secondary">Back to Admin Panel</Button>
        </Link>
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
          <div>
            <h3 className="text-xs font-medium text-gray-500">Search Query</h3>
            <p className="mt-1 text-sm text-gray-900 break-words">{search.search_query}</p>
          </div>
          <div>
            <h3 className="text-xs font-medium text-gray-500">Location</h3>
            <p className="mt-1 text-sm text-gray-900">
              {search.city_name}, {search.state_name}, {search.country}
            </p>
          </div>
          <div>
            <h3 className="text-xs font-medium text-gray-500">Status</h3>
            <p className="mt-1 text-sm text-gray-900">{search.status}</p>
          </div>
          <div>
            <h3 className="text-xs font-medium text-gray-500">Created At</h3>
            <p className="mt-1 text-sm text-gray-900">
              {new Date(search.created_at).toLocaleString()}
            </p>
          </div>
          <div>
            <h3 className="text-xs font-medium text-gray-500">Progress</h3>
            <p className="mt-1 text-sm text-gray-900">
              {search.progress ? `${search.progress.toFixed(2)}%` : 'N/A'}
            </p>
          </div>
          <div>
            <h3 className="text-xs font-medium text-gray-500">User Email</h3>
            <p className="mt-1 text-sm text-gray-900">
              {search.user?.email || 'N/A'}
            </p>
          </div>
          <div>
            <h3 className="text-xs font-medium text-gray-500">Username</h3>
            <p className="mt-1 text-sm text-gray-900">
              {search.user?.username || 'N/A'}
            </p>
          </div>
          <div>
            <h3 className="text-xs font-medium text-gray-500">User ID</h3>
            <p className="mt-1 text-sm text-gray-900">{search.user_id}</p>
          </div>
        </div>

        <div className="mt-4">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Links Status</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-gray-900">{search.total_links}</p>
              <p className="text-xs text-gray-500">Total Links</p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-green-900">{search.completed_links}</p>
              <p className="text-xs text-green-500">Completed Links</p>
            </div>
            <div className="bg-red-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-red-900">{search.failed_links}</p>
              <p className="text-xs text-red-500">Failed Links</p>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Address Normalization</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-gray-900">{search.total_places_for_normalization}</p>
              <p className="text-xs text-gray-500">Total Places</p>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-blue-900">{search.normalized_places_count}</p>
              <p className="text-xs text-blue-500">Normalized Places</p>
            </div>
            <div className="bg-yellow-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-yellow-900">{search.address_normalization_status}</p>
              <p className="text-xs text-yellow-500">Status</p>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Website Scraping</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-gray-900">{search.total_places_for_website_scraping || 'N/A'}</p>
              <p className="text-xs text-gray-500">Total Places</p>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-purple-900">{search.website_scraped_places_count || 'N/A'}</p>
              <p className="text-xs text-purple-500">Scraped Places</p>
            </div>
            <div className="bg-orange-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-orange-900">{search.website_scraping_status || 'N/A'}</p>
              <p className="text-xs text-orange-500">Status</p>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Points Processing Status</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-gray-900">{search.total_points !== undefined ? search.total_points : 'N/A'}</p>
              <p className="text-xs text-gray-500">Total Points</p>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-blue-900">{search.processed_points !== undefined ? search.processed_points : 'N/A'}</p>
              <p className="text-xs text-blue-500">Processed Points</p>
            </div>
          </div>
        </div>

        {/* Map showing the search area */}
        {search.city && (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-500 mb-3">Search Area</h4>
            <div className="rounded-lg overflow-hidden border border-gray-200">
              <BoundingBoxMap
                key={`admin-search-${search.id}-${search.city.lat}-${search.city.lng}`}
                lat={search.city.lat}
                lng={search.city.lng}
                lat_start={search.city.lat_start}
                long_start={search.city.long_start}
                lat_end={search.city.lat_end}
                long_end={search.city.long_end}
                display_name={search.city.display_name}
                height="300px"
                forceRefresh={true}
                disableWarning={false}
              />
            </div>
          </div>
        )}

      </div>
    </Card>
  );
};

export default AdminSearchDetail; 